// ============================================================
// AvatarChooserModal.tsx
// Avatar chooser: a PHOTO (uploaded + client-cropped) OR a styled
// initial (solid / soft / icon + colour). Photo takes precedence; the
// style/colour is the fallback shown when no photo is set.
//
// Two modes in one modal:
//   • 'chooser' — live preview + style + colour + Upload/Remove photo +
//                 Save (persists style+colour via updateMeApi).
//   • 'crop'    — react-easy-crop over the picked image; Save photo draws
//                 the crop to a 256×256 canvas → webp blob → presigned
//                 PUT to R2 (reuses the FileUpload pattern) → confirm.
//
// Photo save/remove persist immediately (their own endpoints) and update
// authUser; style/colour still commit on the chooser's Save button.
// ============================================================
import React, { useCallback, useEffect, useRef, useState } from 'react'
import Cropper from 'react-easy-crop'
import type { Area } from 'react-easy-crop'
import { useCasevoStore } from '../../store/useCasevoStore'
import {
  updateMeApi,
  requestAvatarUploadUrlApi,
  confirmAvatarApi,
  removeAvatarApi,
} from '../../api/casevoApi'
import Avatar, { PRESET_AVATAR_COLOURS, DEFAULT_AVATAR_COLOUR } from './Avatar'
import { PresetColourPicker, type PresetSwatch } from './ColourPicker'

// The avatar presets as swatches for the shared colour picker (preset mode —
// avatars keep their fixed set, each readable under white initials).
const AVATAR_COLOUR_NAMES: Record<string, string> = {
  '#2d6a4f': 'Casevo green', '#334155': 'Slate', '#57534e': 'Stone', '#0f766e': 'Teal',
  '#0369a1': 'Sky', '#4338ca': 'Indigo', '#be123c': 'Rose', '#b45309': 'Amber',
}
const AVATAR_SWATCHES: readonly PresetSwatch[] = PRESET_AVATAR_COLOURS.map((c) => ({ value: c, name: AVATAR_COLOUR_NAMES[c] ?? c.toUpperCase(), hex: c }))

type AvatarStyle = 'solid' | 'soft' | 'icon'
const STYLE_OPTIONS: { value: AvatarStyle; label: string }[] = [
  { value: 'solid', label: 'Solid' },
  { value: 'soft', label: 'Soft' },
  { value: 'icon', label: 'Icon' },
]
const OUTPUT_SIZE = 256 // px — the square we export the crop to

interface AvatarChooserModalProps {
  isOpen: boolean
  onClose: () => void
}

// Load an image element from a data URL (for canvas drawing).
const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image()
    img.addEventListener('load', () => resolve(img))
    img.addEventListener('error', (e) => reject(e))
    img.src = url
  })

// Draw the selected crop area to a square canvas and export a blob.
// Prefers webp (small); the browser falls back to png if webp isn't
// supported — both are accepted by the backend.
const getCroppedBlob = async (imageSrc: string, cropPixels: Area): Promise<Blob> => {
  const image = await createImage(imageSrc)
  const canvas = document.createElement('canvas')
  canvas.width = OUTPUT_SIZE
  canvas.height = OUTPUT_SIZE
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Canvas not supported')
  ctx.drawImage(
    image,
    cropPixels.x, cropPixels.y, cropPixels.width, cropPixels.height,
    0, 0, OUTPUT_SIZE, OUTPUT_SIZE,
  )
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => { if (blob) resolve(blob); else reject(new Error('Could not process image')) },
      'image/webp',
      0.9,
    )
  })
}

const AvatarChooserModal: React.FC<AvatarChooserModalProps> = ({ isOpen, onClose }) => {
  const authUser = useCasevoStore((s) => s.authUser)
  const setAuthUser = useCasevoStore((s) => s.setAuthUser)

  const [mode, setMode] = useState<'chooser' | 'crop'>('chooser')

  // Style/colour working selection — LOCAL until the chooser Save.
  const [selColour, setSelColour] = useState<string>(DEFAULT_AVATAR_COLOUR)
  const [selStyle, setSelStyle] = useState<AvatarStyle>('solid')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Crop state.
  const [imageSrc, setImageSrc] = useState<string | null>(null)
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null)
  const [uploading, setUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Re-seed style/colour from saved values, and reset to the chooser,
  // each time the modal opens.
  useEffect(() => {
    if (!isOpen || !authUser) return
    setSelColour(
      authUser.avatarColour && authUser.avatarColour.length > 0
        ? authUser.avatarColour
        : DEFAULT_AVATAR_COLOUR,
    )
    setSelStyle(authUser.avatarStyle ?? 'solid')
    setMode('chooser')
    setImageSrc(null)
    setError(null)
  }, [isOpen, authUser])

  // Esc closes (chooser only — in crop mode Esc backs out to chooser).
  useEffect(() => {
    if (!isOpen) return
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape') return
      if (mode === 'crop') { setMode('chooser'); setImageSrc(null) }
      else onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [isOpen, onClose, mode])

  const onCropComplete = useCallback((_area: Area, areaPixels: Area) => {
    setCroppedAreaPixels(areaPixels)
  }, [])

  if (!isOpen || !authUser) return null

  const hasPhoto = !!authUser.avatarPhotoUrl

  // Persist style + colour (fallback initial).
  const handleSaveStyle = async (): Promise<void> => {
    const prevColour = authUser.avatarColour
    const prevStyle = authUser.avatarStyle
    setAuthUser({ ...authUser, avatarColour: selColour, avatarStyle: selStyle })
    setSaving(true)
    setError(null)
    try {
      const updated = await updateMeApi({ avatarColour: selColour, avatarStyle: selStyle })
      setAuthUser(updated)
      onClose()
    } catch (err) {
      setAuthUser({ ...authUser, avatarColour: prevColour, avatarStyle: prevStyle })
      setError(err instanceof Error ? err.message : 'Could not save your avatar. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  // File pick → read as data URL → open the crop step.
  const handleFilePicked = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0]
    // Reset the input so picking the SAME file again still fires onChange.
    e.target.value = ''
    if (!file) return
    setError(null)
    const reader = new FileReader()
    reader.onload = () => {
      setImageSrc(typeof reader.result === 'string' ? reader.result : null)
      setCrop({ x: 0, y: 0 })
      setZoom(1)
      setMode('crop')
    }
    reader.onerror = () => setError('Could not read that image.')
    reader.readAsDataURL(file)
  }

  // Crop → blob → presigned PUT → confirm → update authUser.
  const handleSavePhoto = async (): Promise<void> => {
    if (!imageSrc || !croppedAreaPixels) return
    setUploading(true)
    setError(null)
    try {
      const blob = await getCroppedBlob(imageSrc, croppedAreaPixels)
      const { uploadUrl, r2Key } = await requestAvatarUploadUrlApi({ mimeType: blob.type, sizeBytes: blob.size })
      // Direct PUT to R2 — plain fetch, ONLY Content-Type (an auth header
      // would break the presigned signature). Same as FileUpload.
      const put = await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': blob.type }, body: blob })
      if (!put.ok) throw new Error(`Upload failed (status ${put.status})`)
      const { avatarPhotoUrl } = await confirmAvatarApi(r2Key)
      setAuthUser({ ...authUser, avatarPhotoUrl })
      setImageSrc(null)
      setMode('chooser')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save your photo. Please try again.')
    } finally {
      setUploading(false)
    }
  }

  const handleRemovePhoto = async (): Promise<void> => {
    setUploading(true)
    setError(null)
    try {
      const { avatarPhotoUrl } = await removeAvatarApi()
      setAuthUser({ ...authUser, avatarPhotoUrl })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not remove your photo. Please try again.')
    } finally {
      setUploading(false)
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 px-4 py-6"
      onClick={onClose}
    >
      <div
        className="bg-casevo-surface rounded-lg border border-casevo-border w-full max-w-md max-h-[90vh] overflow-y-auto p-6"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Choose your avatar"
      >
        {mode === 'crop' ? (
          // ── CROP STEP ──────────────────────────────────────
          <>
            <h3 className="text-sm font-medium text-casevo-forest uppercase tracking-widest mb-4">
              Position your photo
            </h3>
            <div className="relative w-full h-64 bg-black/80 rounded-lg overflow-hidden mb-4">
              {imageSrc && (
                <Cropper
                  image={imageSrc}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  cropShape="round"
                  showGrid={false}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                />
              )}
            </div>
            <label className="block text-xs uppercase tracking-widest text-casevo-brand mb-1">Zoom</label>
            <input
              type="range"
              min={1}
              max={3}
              step={0.01}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              disabled={uploading}
              className="w-full mb-4 accent-casevo-brand"
              aria-label="Zoom"
            />
            {error && <p className="text-xs text-red-600 dark:text-red-300 mb-4">{error}</p>}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => { setMode('chooser'); setImageSrc(null); setError(null) }}
                disabled={uploading}
                className="flex-1 border border-casevo-border text-casevo-forest text-sm py-2 rounded hover:border-casevo-mid transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => { void handleSavePhoto() }}
                disabled={uploading || !croppedAreaPixels}
                className="flex-1 bg-casevo-primary text-white text-sm py-2 rounded hover:bg-casevo-primary-hover transition-colors disabled:opacity-50"
              >
                {uploading ? 'Saving…' : 'Save photo'}
              </button>
            </div>
          </>
        ) : (
          // ── CHOOSER STEP ───────────────────────────────────
          <>
            <h3 className="text-sm font-medium text-casevo-forest uppercase tracking-widest mb-5">
              Your avatar
            </h3>

            {/* LIVE PREVIEW — shows the PHOTO when set, else the styled
                initial. Photo takes precedence (matches app-wide). */}
            <div className="flex flex-col items-center gap-2 mb-6">
              <Avatar
                size="lg"
                firstName={authUser.firstName}
                lastName={authUser.lastName}
                email={authUser.email}
                colour={selColour}
                style={selStyle}
                photoUrl={authUser.avatarPhotoUrl}
              />
              <p className="text-xs text-casevo-mid uppercase tracking-widest">Preview</p>
            </div>

            {/* PHOTO controls. */}
            <div className="flex gap-3 mb-2">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading || saving}
                className="flex-1 bg-casevo-primary text-white text-sm py-2 rounded hover:bg-casevo-primary-hover transition-colors disabled:opacity-50"
              >
                {hasPhoto ? 'Replace photo' : 'Upload photo'}
              </button>
              {hasPhoto && (
                <button
                  type="button"
                  onClick={() => { void handleRemovePhoto() }}
                  disabled={uploading || saving}
                  className="flex-1 border border-casevo-border text-casevo-forest text-sm py-2 rounded hover:text-casevo-danger hover:border-red-500 transition-colors disabled:opacity-50"
                >
                  {uploading ? 'Removing…' : 'Remove photo'}
                </button>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleFilePicked}
              className="hidden"
            />
            <p className="text-xs text-casevo-mid mb-6">
              {hasPhoto
                ? 'Your photo is shown. Style and colour below apply if you remove it.'
                : 'Upload a photo, or pick a style and colour below.'}
            </p>

            {/* STYLE — the fallback initial (no photoUrl passed, so each
                card previews the styled initial). */}
            <p className="text-sm text-casevo-forest mb-2">Style</p>
            <div className="flex gap-2 mb-6">
              {STYLE_OPTIONS.map((opt) => {
                const active = selStyle === opt.value
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setSelStyle(opt.value)}
                    aria-pressed={active}
                    className={`flex-1 flex flex-col items-center gap-2 rounded-lg border px-2 py-3 transition-colors ${
                      active ? 'border-casevo-brand bg-casevo-mint/30' : 'border-casevo-border hover:border-casevo-mid'
                    }`}
                  >
                    <Avatar
                      size="md"
                      firstName={authUser.firstName}
                      lastName={authUser.lastName}
                      email={authUser.email}
                      colour={selColour}
                      style={opt.value}
                    />
                    <span className="text-xs uppercase tracking-widest text-casevo-forest">{opt.label}</span>
                  </button>
                )
              })}
            </div>

            {/* COLOUR — the shared colour picker in preset mode (the presets). */}
            <p className="text-sm text-casevo-forest mb-2" aria-hidden="true">Colour</p>
            <div className="mb-6">
              <PresetColourPicker label="Avatar colour" value={selColour} swatches={AVATAR_SWATCHES} onPick={setSelColour} />
            </div>

            {error && <p className="text-xs text-red-600 dark:text-red-300 mb-4">{error}</p>}

            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={saving || uploading}
                className="flex-1 border border-casevo-border text-casevo-forest text-sm py-2 rounded hover:border-casevo-mid transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => { void handleSaveStyle() }}
                disabled={saving || uploading}
                className="flex-1 bg-casevo-primary text-white text-sm py-2 rounded hover:bg-casevo-primary-hover transition-colors disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default AvatarChooserModal
